import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, CheckCircle, XCircle, AlertTriangle, Image } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { quotationService } from '@/lib/supabase/quotations';
import { tripService } from '@/lib/supabase/trips';
import { storageService } from '@/lib/supabase/storage';

enum MigrationStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  SUCCESS = 'success',
  FAILURE = 'failure',
}

interface MigrationState {
  quotationImages: MigrationStatus;
  tripImages: MigrationStatus;
}

interface MigrationStats {
  total: number;
  migrated: number;
  failed: number;
}

export default function ImageMigration() {
  const { toast } = useToast();
  const [migrationState, setMigrationState] = useState<MigrationState>({
    quotationImages: MigrationStatus.PENDING,
    tripImages: MigrationStatus.PENDING,
  });
  
  const [quotationStats, setQuotationStats] = useState<MigrationStats>({
    total: 0, 
    migrated: 0, 
    failed: 0
  });
  const [tripStats, setTripStats] = useState<MigrationStats>({
    total: 0, 
    migrated: 0, 
    failed: 0
  });

  // Função para migrar imagens de cotações
  const migrateQuotationImages = async () => {
    setMigrationState(prev => ({ ...prev, quotationImages: MigrationStatus.RUNNING }));
    let totalImages = 0;
    let migratedImages = 0;
    let failedImages = 0;
    
    try {
      // Buscar todas as cotações
      const quotations = await quotationService.getQuotations();
      
      // Processar cada cotação
      for (const quotation of quotations) {
        try {
          // Processar imagens de capa
          if (quotation.cover_images && quotation.cover_images.length > 0) {
            totalImages += quotation.cover_images.length;
            
            // Migrar imagens de capa
            const coverImages = [...quotation.cover_images]; // Criar cópia segura
            const newCoverImages = await storageService.migrateImages(
              coverImages,
              `quotations/${quotation.id}/covers`
            );
            
            // Contar imagens migradas e falhas
            migratedImages += newCoverImages.filter((url, index) => 
              url !== coverImages[index]).length;
            failedImages += newCoverImages.filter((url, index) => 
              url === coverImages[index]).length;
            
            // Atualizar cotação com novas URLs
            if (JSON.stringify(newCoverImages) !== JSON.stringify(quotation.cover_images)) {
              await quotationService.updateQuotation(quotation.id, { 
                cover_images: newCoverImages 
              });
            }
          }
          
          // Processar imagens de páginas
          if (quotation.pages && quotation.pages.length > 0) {
            for (let pageIndex = 0; pageIndex < quotation.pages.length; pageIndex++) {
              const page = quotation.pages[pageIndex];
              
              // Imagens de página
              if (page.images && page.images.length > 0) {
                totalImages += page.images.length;
                
                // Migrar imagens da página
                const newPageImages = await storageService.migrateImages(
                  page.images,
                  `quotations/${quotation.id}/pages/${page.id}`
                );
                
                // Contar imagens migradas e falhas
                migratedImages += newPageImages.filter((url, index) => 
                  url !== page.images[index]).length;
                failedImages += newPageImages.filter((url, index) => 
                  url === page.images[index]).length;
                
                // Atualizar as URLs das imagens na página
                if (JSON.stringify(newPageImages) !== JSON.stringify(page.images)) {
                  quotation.pages[pageIndex].images = newPageImages;
                }
              }
              
              // Processar outras seções com imagens (acomodações, passeios, etc.)
              // Implementação dependeria da estrutura exata dos seus dados
            }
            
            // Atualizar a cotação com todas as páginas atualizadas
            await quotationService.updateQuotation(quotation.id, { 
              pages: quotation.pages 
            });
          }
        } catch (error) {
          console.error(`Erro ao processar imagens da cotação ${quotation.id}:`, error);
          failedImages++;
        }
        
        // Atualizar estatísticas em tempo real
        setQuotationStats({
          total: totalImages,
          migrated: migratedImages,
          failed: failedImages
        });
      }
      
      setMigrationState(prev => ({ ...prev, quotationImages: MigrationStatus.SUCCESS }));
      toast({
        title: "Migração Concluída",
        description: `${migratedImages} de ${totalImages} imagens foram migradas com sucesso para o Supabase Storage.`,
      });
    } catch (error) {
      console.error("Erro na migração de imagens de cotações:", error);
      setMigrationState(prev => ({ ...prev, quotationImages: MigrationStatus.FAILURE }));
      toast({
        title: "Erro",
        description: "Ocorreu um erro durante a migração de imagens de cotações.",
        variant: "destructive",
      });
    }
  };

  // Função para migrar imagens de viagens
  const migrateTripImages = async () => {
    setMigrationState(prev => ({ ...prev, tripImages: MigrationStatus.RUNNING }));
    let totalImages = 0;
    let migratedImages = 0;
    let failedImages = 0;
    
    try {
      // Buscar todas as viagens
      const trips = await tripService.getTrips();
      
      // Processar cada viagem
      for (const trip of trips) {
        try {
          // Processar imagens de capa
          if (trip.cover_images && trip.cover_images.length > 0) {
            totalImages += trip.cover_images.length;
            
            // Migrar imagens de capa
            const coverImages = [...trip.cover_images]; // Criar cópia segura
            const newCoverImages = await storageService.migrateImages(
              coverImages,
              `trips/${trip.id}/covers`
            );
            
            // Contar imagens migradas e falhas
            migratedImages += newCoverImages.filter((url, index) => 
              url !== coverImages[index]).length;
            failedImages += newCoverImages.filter((url, index) => 
              url === coverImages[index]).length;
            
            // Atualizar viagem com novas URLs
            if (JSON.stringify(newCoverImages) !== JSON.stringify(trip.cover_images)) {
              await tripService.updateTrip(trip.id, { 
                cover_images: newCoverImages 
              });
            }
          }
          
          // Outras seções com imagens que precisam ser processadas
          // Implemente de acordo com a estrutura de dados das viagens
        } catch (error) {
          console.error(`Erro ao processar imagens da viagem ${trip.id}:`, error);
          failedImages++;
        }
        
        // Atualizar estatísticas em tempo real
        setTripStats({
          total: totalImages,
          migrated: migratedImages,
          failed: failedImages
        });
      }
      
      setMigrationState(prev => ({ ...prev, tripImages: MigrationStatus.SUCCESS }));
      toast({
        title: "Migração Concluída",
        description: `${migratedImages} de ${totalImages} imagens de viagens foram migradas com sucesso.`,
      });
    } catch (error) {
      console.error("Erro na migração de imagens de viagens:", error);
      setMigrationState(prev => ({ ...prev, tripImages: MigrationStatus.FAILURE }));
      toast({
        title: "Erro",
        description: "Ocorreu um erro durante a migração de imagens de viagens.",
        variant: "destructive",
      });
    }
  };

  // Migrar todas as imagens
  const migrateAllImages = async () => {
    // Reset status
    setMigrationState({
      quotationImages: MigrationStatus.PENDING,
      tripImages: MigrationStatus.PENDING,
    });
    
    // Migrar cotações primeiro
    await migrateQuotationImages();
    
    // Depois migrar viagens
    await migrateTripImages();
    
    toast({
      title: "Migração Completa",
      description: "Todas as imagens foram processadas.",
    });
  };

  // Renderizar o status da migração
  const renderStatus = (status: MigrationStatus) => {
    switch (status) {
      case MigrationStatus.PENDING:
        return <Badge variant="outline">Pendente</Badge>;
      case MigrationStatus.RUNNING:
        return <Badge variant="secondary" className="flex items-center"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Migrando...</Badge>;
      case MigrationStatus.SUCCESS:
        return <Badge variant="default" className="bg-green-500 flex items-center"><CheckCircle className="w-3 h-3 mr-1" /> Concluído</Badge>;
      case MigrationStatus.FAILURE:
        return <Badge variant="destructive" className="flex items-center"><XCircle className="w-3 h-3 mr-1" /> Falha</Badge>;
    }
  };

  return (
    <AdminLayout>
      <div className="container mx-auto py-8">
        <h1 className="text-2xl font-bold mb-6">Migração de Imagens para Supabase Storage</h1>
        
        <Alert className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Informação</AlertTitle>
          <AlertDescription>
            Esta página permite migrar imagens de URLs externas e localStorage para o Supabase Storage.
            As imagens serão baixadas e enviadas para o Supabase, garantindo que permaneçam disponíveis mesmo se as fontes originais forem removidas.
          </AlertDescription>
        </Alert>
        
        <Tabs defaultValue="quotations" className="mb-6">
          <TabsList>
            <TabsTrigger value="quotations">Cotações</TabsTrigger>
            <TabsTrigger value="trips">Viagens</TabsTrigger>
          </TabsList>
          
          <TabsContent value="quotations">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Migração de Imagens de Cotações
                </CardTitle>
                <CardDescription>
                  Migre todas as imagens de cotações para o Supabase Storage.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-gray-600 mb-2">Status: {renderStatus(migrationState.quotationImages)}</p>
                    {migrationState.quotationImages === MigrationStatus.RUNNING && (
                      <div className="flex flex-col gap-1">
                        <p className="text-sm text-gray-500">Total: {quotationStats.total}</p>
                        <p className="text-sm text-gray-500">Migradas: {quotationStats.migrated}</p>
                        <p className="text-sm text-gray-500">Falhas: {quotationStats.failed}</p>
                      </div>
                    )}
                  </div>
                  <Button 
                    onClick={migrateQuotationImages}
                    disabled={migrationState.quotationImages === MigrationStatus.RUNNING}
                    className="bg-voah-teal hover:bg-voah-teal-dark"
                  >
                    {migrationState.quotationImages === MigrationStatus.RUNNING ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Migrando...
                      </>
                    ) : (
                      "Migrar Imagens de Cotações"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="trips">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Migração de Imagens de Viagens
                </CardTitle>
                <CardDescription>
                  Migre todas as imagens de viagens para o Supabase Storage.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-gray-600 mb-2">Status: {renderStatus(migrationState.tripImages)}</p>
                    {migrationState.tripImages === MigrationStatus.RUNNING && (
                      <div className="flex flex-col gap-1">
                        <p className="text-sm text-gray-500">Total: {tripStats.total}</p>
                        <p className="text-sm text-gray-500">Migradas: {tripStats.migrated}</p>
                        <p className="text-sm text-gray-500">Falhas: {tripStats.failed}</p>
                      </div>
                    )}
                  </div>
                  <Button 
                    onClick={migrateTripImages}
                    disabled={migrationState.tripImages === MigrationStatus.RUNNING}
                    className="bg-voah-teal hover:bg-voah-teal-dark"
                  >
                    {migrationState.tripImages === MigrationStatus.RUNNING ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Migrando...
                      </>
                    ) : (
                      "Migrar Imagens de Viagens"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        <Card>
          <CardHeader>
            <CardTitle>Migração Completa</CardTitle>
            <CardDescription>
              Migre todas as imagens de uma só vez para o Supabase Storage.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Esta operação migra todas as imagens de cotações e viagens para o Supabase Storage.
              O processo pode levar alguns minutos, dependendo da quantidade de imagens.
            </p>
          </CardContent>
          <CardFooter className="flex justify-end">
            <Button 
              onClick={migrateAllImages}
              disabled={
                migrationState.quotationImages === MigrationStatus.RUNNING || 
                migrationState.tripImages === MigrationStatus.RUNNING
              }
              className="bg-voah-coral hover:bg-voah-coral-dark"
            >
              {migrationState.quotationImages === MigrationStatus.RUNNING || 
               migrationState.tripImages === MigrationStatus.RUNNING ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Migrando...
                </>
              ) : (
                "Migrar Todas as Imagens"
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </AdminLayout>
  );
} 